import { apiClient } from '../client'
import type { PaginatedResponse } from './role.repository'

export interface AiGallerySceneRow {
  id: number
  evaluation_id: string
  typology_key: string | null
  scene_id: number
  report_date: string | null
  store_id: number
  store: string
  city: string
  region: string
  status: string | null
  request_json?: string | null
  response_json?: string | null
}

export type AiGallerySceneFilters = {
  page?: number
  limit?: number
  start_date?: string
  end_date?: string
  store_id?: number
  city_ids?: string
  region_ids?: string
  client_ids?: string
  evaluation_id?: string
  status?: string
}

/** Row from `GET /ai-gallery/scenes/:evaluationId/images` (matches `bkpis_ai_scenes_images`). */
export interface AiGallerySceneImageRow {
  id: number
  evaluation_id: string
  image_name: string
  image_url?: string | null
  photo_meta_data: unknown
  our_faces_count: number
  unknown_faces_count: number
  our_faces_bboxes: unknown
  unknown_faces_bboxes: unknown
  gcs_status?: number
  audit_our_sku_faces?: string | null
  audit_other_sku_faces?: string | null
  comments?: string | null
  audit_at?: string | null
}

class AiGalleryRepository {
  async getScenes(
    page: number = 1,
    limit: number = 50,
    filters?: AiGallerySceneFilters,
  ): Promise<PaginatedResponse<AiGallerySceneRow>> {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', String(limit))

    if (filters) {
      for (const [key, value] of Object.entries(filters)) {
        if (value === undefined || value === null || value === '') continue
        params.set(key, String(value))
      }
    }

    return apiClient.get<PaginatedResponse<AiGallerySceneRow>>(
      `/ai-gallery/scenes?${params.toString()}`,
    )
  }

  /** All images for one evaluation (tenant-scoped on the server). */
  async getSceneImages(evaluationId: string): Promise<AiGallerySceneImageRow[]> {
    const enc = encodeURIComponent(evaluationId)
    return apiClient.get<AiGallerySceneImageRow[]>(`/ai-gallery/scenes/${enc}/images`)
  }

  /** Grouped cards from `bkpis_ai_gallery_scan_history` (store + date + model). */
  async getScanGroups(
    page: number = 1,
    limit: number = 50,
    filters?: AiGalleryScanGroupFilters,
  ): Promise<PaginatedResponse<AiGalleryScanGroupCard>> {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', String(limit))

    if (filters) {
      for (const [key, value] of Object.entries(filters)) {
        if (value === undefined || value === null || value === '') continue
        params.set(key, String(value))
      }
    }

    return apiClient.get<PaginatedResponse<AiGalleryScanGroupCard>>(
      `/ai-gallery-new/groups?${params.toString()}`,
    )
  }

  /** All scan rows for one gallery card (includes unit bboxes). */
  async getScansByGroup(query: {
    store_id: number
    report_date: string
    model_name?: string | null
  }): Promise<AiGalleryScanHistoryRow[]> {
    const params = new URLSearchParams()
    params.set('store_id', String(query.store_id))
    params.set('report_date', query.report_date)
    if (query.model_name != null && query.model_name !== '') {
      params.set('model_name', query.model_name)
    }
    return apiClient.get<AiGalleryScanHistoryRow[]>(`/ai-gallery-new/scans?${params.toString()}`)
  }

  /** Excel export for current gallery filters (includes store relations). */
  async exportScanGroupsToExcel(filters?: AiGalleryScanGroupFilters): Promise<Blob> {
    const params = new URLSearchParams()
    if (filters) {
      for (const [key, value] of Object.entries(filters)) {
        if (value === undefined || value === null || value === '') continue
        params.set(key, String(value))
      }
    }
    const qs = params.toString()
    return apiClient.getBlob(
      qs ? `/ai-gallery-new/export/excel?${qs}` : '/ai-gallery-new/export/excel',
      true,
      { showSuccessToast: true, successMessage: 'Exported Successfully' },
    )
  }
}

export type AiGalleryScanGroupFilters = {
  page?: number
  limit?: number
  start_date?: string
  end_date?: string
  store_id?: number
  store_ids?: string
  city_ids?: string
  region_ids?: string
  channel_ids?: string
  chain_ids?: string
  store_format_ids?: string
  store_class_ids?: string
  category_ids?: string
  subcategory_ids?: string
  model_name?: string
}

/** One gallery card from grouped scan history. */
export interface AiGalleryScanGroupCard {
  store_id: number
  report_date: string
  model_name: string | null
  store_name: string | null
  image_count: number
  recognised_sku?: number | string | null
  recognised_faces?: number | string | null
  images?: string | null
}

/** Row from `GET /ai-gallery-new/scans` (`bkpis_ai_gallery_scan_history`). */
export interface AiGalleryScanHistoryRow {
  id: number
  report_date: string | null
  tenant_id: number | null
  user_id: number | null
  store_id: number | null
  visit_id: number | null
  typology_key: string | null
  model_name: string | null
  image_name: string | null
  image_url?: string | null
  evaluation_id: string | null
  photo_meta: unknown
  request_json: unknown
  response_json: unknown
  our_units_count: number
  our_units_bboxes: unknown
  unknown_units_count: number
  unknown_units_bboxes: unknown
  status: string | null
  gcs_status: number
  created_at: string | null
  store_name: string | null
  static_bucket_url?: string | null
  dynamic_bucket_url?: string | null
  sku_photo_by_id?: Record<string, string>
}

export const aiGalleryRepository = new AiGalleryRepository()
